import { CreateFunction } from "@hex-insights/app-modules";
import { ClassNameProps, getObjectValues, StyleProps } from "@hex-insights/core";
import { Form, FormState, SubmitButton } from "@hex-insights/forms";
import { CourseAssessmentCreateMutation } from "../../../Utilities";
import { CourseAssessmentFormState, CourseAssessmentFormValues } from "../../FormUtilities";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import { useCreateFormControl, useCreateFormSubmission } from "./hooks";

export type ControlledCreateProps = Omit<
	CreateProps<CourseAssessmentCreateMutation["createCourseAssessment"]>,
	"formState" | "create"
> & {
	formStateArgs?: CourseAssessmentFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the CourseAssessment model using an internally managed form state.
 */
export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <Create formState={formState} create={create} {...props} />;
}

export type CreateProps<T> = {
	formState: FormState<CourseAssessmentFormValues.Create>;
	create: CreateFunction<CourseAssessmentFormValues.Create, T>;
	onSuccess: (formState: FormState<CourseAssessmentFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<CourseAssessmentFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the CourseAssessment model using the given form state.
 */
export function Create<T>({ formState, create, onSuccess, formNameSuffix, ...props }: CreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "courseAssessment.create" + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column style={{ width: "100%" }}>
				<Field.CourseAssessmentCategory.Radio formState={formState} />
				<Field.Name formState={formState} />
				<Field.Description formState={formState} style={{ width: "100%" }} />
				<Field.MediaAttachments.SubForm formState={formState} style={{ width: "100%" }} />
				<Field.TotalPoints formState={formState} />
				<Field.CourseAssessmentTimings.SubForm formState={formState} label="Timing" />

				<Form.ActionBar>
					<SubmitButton
						submissionStatus={formState.submissionStatus}
						onClick={formState.onSubmitWrapper(onSubmit)}
						disabled={getObjectValues(formState.submissionLocks).some((e) => e)}
					>
						Create Assessment
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}
